import { Ionicons } from '@expo/vector-icons';
import React, { useMemo } from 'react';
import { View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { Card } from '@/components/ui/Card';
import { useThemePalette } from '@/config/theme';
import type { MatchBroadcast } from '@/lib/data/provider';

/** How many channels we show at most. */
const MAX_CHANNELS = 6;

/** MENA / Arabic-region country codes whose broadcasts surface first. */
const MENA_CODES = new Set([
  'SA',
  'EG',
  'AE',
  'QA',
  'KW',
  'BH',
  'OM',
  'JO',
  'IQ',
  'MA',
  'DZ',
  'TN',
  'LY',
  'LB',
]);

/**
 * An ISO-3166 alpha-2 country code → flag emoji (regional indicator pair). Only
 * applied for a clean two-letter alpha code; anything else yields '' so we never
 * render mojibake next to the channel name. (Mirrors RefereeCard's helper.)
 */
function flagFromCountryCode(code: string | null | undefined): string {
  if (!code || !/^[a-zA-Z]{2}$/.test(code)) return '';
  const upper = code.toUpperCase();
  const base = 0x1f1e6; // regional indicator 'A'
  return String.fromCodePoint(
    base + (upper.charCodeAt(0) - 65),
    base + (upper.charCodeAt(1) - 65),
  );
}

/** Whether a broadcast is MENA-relevant: a beIN channel OR a MENA country code. */
function isMena(broadcast: MatchBroadcast): boolean {
  const name = broadcast.channel_name?.toLowerCase() ?? '';
  return name.includes('bein') || MENA_CODES.has((broadcast.country_code ?? '').toUpperCase());
}

/** A single channel chip: flag emoji + channel name. */
function ChannelChip({ broadcast }: { broadcast: MatchBroadcast }) {
  const palette = useThemePalette();
  const flag = flagFromCountryCode(broadcast.country_code);
  return (
    <View
      className="flex-row items-center gap-1.5 rounded-full border px-3 py-1.5"
      style={{ backgroundColor: palette.inset, borderColor: palette.line }}
    >
      {flag ? <AppText variant="caption">{flag}</AppText> : null}
      <AppText variant="caption" weight="medium" className="text-soft" numberOfLines={1}>
        {broadcast.channel_name}
      </AppText>
    </View>
  );
}

/**
 * Where to watch: the fixture's TV / streaming broadcasts as a wrapped row of
 * chips (flag emoji + channel name). MENA / Arabic-region channels are sorted to
 * the front (beIN, or a Gulf/North-African country code) so the primary audience
 * sees its providers first, deduped by channel name and capped at six. The caller
 * hides the block when `broadcasts` is null; this also self-hides when the list
 * resolves to nothing.
 */
export function WhereToWatch({ broadcasts }: { broadcasts: MatchBroadcast[] }) {
  const palette = useThemePalette();
  const { t } = useTranslation();

  const channels = useMemo(() => {
    // Dedupe by channel name (case-insensitive), keeping the first occurrence.
    const seen = new Set<string>();
    const unique: MatchBroadcast[] = [];
    for (const broadcast of broadcasts) {
      const name = broadcast.channel_name?.trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(broadcast);
    }
    // MENA-first; a stable sort keeps each group's original order.
    return unique
      .map((broadcast, index) => ({ broadcast, index }))
      .sort((a, b) => {
        const menaA = isMena(a.broadcast) ? 0 : 1;
        const menaB = isMena(b.broadcast) ? 0 : 1;
        if (menaA !== menaB) return menaA - menaB;
        return a.index - b.index;
      })
      .map((entry) => entry.broadcast)
      .slice(0, MAX_CHANNELS);
  }, [broadcasts]);

  if (channels.length === 0) return null;

  return (
    <Card className="overflow-hidden">
      <View className="flex-row items-center gap-2 px-4 pb-1 pt-3">
        <Ionicons name="tv-outline" size={14} color={palette.voltBright} />
        <AppText variant="micro" weight="bold" className="uppercase tracking-widest text-muted">
          {t('match.whereToWatch')}
        </AppText>
      </View>
      <View className="flex-row flex-wrap gap-2 px-4 pb-3.5 pt-2">
        {channels.map((broadcast, index) => (
          <ChannelChip key={`${broadcast.channel_name}-${index}`} broadcast={broadcast} />
        ))}
      </View>
    </Card>
  );
}
